import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { PERMISSION_GROUPS, getModuleCrudActions, DEFAULT_CRUD_ACTIONS } from "../Components/Permission";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

/** Shared sizing so hidden Delete slots keep View/Create/Update aligned */
const CRUD_BUTTON_STYLE = {
  borderRadius: "16px",
  fontSize: "11px",
  fontWeight: 600,
  padding: "2px 10px",
  textTransform: "capitalize",
  minWidth: "64px",
  boxSizing: "border-box",
};

/** Scope-only keys — not saved on the role permissions array. */
const SCOPE_CHILD_KEYS = new Set([
  "supplier_master_view",
  "supplier_master_all",
  "customer_master_view",
  "customer_master_all",
]);

/**
 * Menu/action flags saved on permissions[], but not as RoleAccess modules.
 * (e.g. Customer Order Report Import / Export buttons)
 */
const FLAG_PERMISSION_KEYS = new Set([
  "tracker_update_import",
  "tracker_update_export",
]);

const isNonModulePermission = (key) =>
  SCOPE_CHILD_KEYS.has(key) || FLAG_PERMISSION_KEYS.has(key);

const defaultCrud = () => ({
  view: true,
  create: false,
  update: false,
  delete: false,
});

/** Map Master permission keys → RoleAccess module name */
const toModuleKey = (key) => {
  if (key === "supplier_master_view" || key === "supplier_master_all") {
    return "supplier_master";
  }
  if (key === "customer_master_view" || key === "customer_master_all") {
    return "customer_master";
  }
  if (key === "Sales Approval") return "sales_approval";
  if (key === "supply_master") return "supplier_master";
  return String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
};

/** Map module / alias keys back to the catalog key stored on role.permissions */
const toCatalogPermissionKey = (key) => {
  const raw = String(key || "").trim();
  if (!raw || SCOPE_CHILD_KEYS.has(raw)) return "";
  if (FLAG_PERMISSION_KEYS.has(raw)) return raw;

  const moduleKey = toModuleKey(raw);
  for (const group of PERMISSION_GROUPS) {
    for (const perm of group.permissions) {
      if (SCOPE_CHILD_KEYS.has(perm.key)) continue;
      if (perm.key === raw || toModuleKey(perm.key) === moduleKey) {
        return perm.key;
      }
    }
  }
  return raw;
};

const isKeySelected = (selected, permKey) => {
  if (!Array.isArray(selected) || !permKey) return false;
  if (selected.includes(permKey)) return true;
  const moduleKey = toModuleKey(permKey);
  return selected.some((key) => toModuleKey(key) === moduleKey);
};

/** Same labels as module checkboxes (e.g. Purchase Request, not procurement_tracker) */
const MODULE_LABEL_BY_KEY = (() => {
  const map = {};
  PERMISSION_GROUPS.forEach((group) => {
    group.permissions.forEach((perm) => {
      if (FLAG_PERMISSION_KEYS.has(perm.key) || SCOPE_CHILD_KEYS.has(perm.key)) {
        return;
      }
      const moduleKey = toModuleKey(perm.key);
      if (moduleKey && !map[moduleKey]) {
        map[moduleKey] = perm.label;
      }
    });
  });
  return map;
})();

const moduleLabel = (moduleKey) =>
  MODULE_LABEL_BY_KEY[moduleKey] ||
  String(moduleKey || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const parseApiResponse = (raw) => {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
};

const CrudToggleRow = ({ moduleKey, crud, disabled, onChange }) => {
  const allowed = new Set(getModuleCrudActions(moduleKey));
  return (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: "6px" }}>
      <span
        style={{
          fontSize: "10px",
          fontWeight: 700,
          color: "#1199B2",
          textTransform: "uppercase",
          letterSpacing: "0.03em",
          marginRight: "2px",
        }}
      >
        Actions
      </span>
      {DEFAULT_CRUD_ACTIONS.map((action) => {
        if (!allowed.has(action)) {
          // Keep empty slot so View/Create/Update stay aligned with other modules
          return (
            <span
              key={`${moduleKey}-${action}-spacer`}
              aria-hidden="true"
              className="btn btn-sm"
              style={{
                ...CRUD_BUTTON_STYLE,
                visibility: "hidden",
                pointerEvents: "none",
                border: "1px solid transparent",
              }}
            >
              {action}
            </span>
          );
        }

        const active = Boolean(crud[action]);
        return (
          <button
            key={`${moduleKey}-${action}`}
            type="button"
            disabled={disabled}
            onClick={() => onChange(moduleKey, { [action]: !active })}
            className="btn btn-sm"
            style={{
              ...CRUD_BUTTON_STYLE,
              border: active ? "1px solid #1199B2" : "1px solid #ced4da",
              backgroundColor: active ? "#1199B2" : "#fff",
              color: active ? "#fff" : "#495057",
            }}
          >
            {action}
          </button>
        );
      })}
    </div>
  );
};

const EditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [role, setRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [moduleCrud, setModuleCrud] = useState({});
  const [roleAccessId, setRoleAccessId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const selectedModuleCount = useMemo(
    () =>
      new Set(
        selectedPermissions
          .filter((key) => !isNonModulePermission(key))
          .map((key) => toModuleKey(key))
          .concat(
            Object.keys(moduleCrud).filter((mod) => !isNonModulePermission(mod)),
          ),
      ).size,
    [selectedPermissions, moduleCrud],
  );

  const setCrudForModule = (moduleKey, patch) => {
    setModuleCrud((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || defaultCrud()), ...patch },
    }));
  };

  const applyAccessRows = (rows, permissions) => {
    const next = {};
    if (Array.isArray(rows) && rows.length > 0) {
      rows.forEach((row) => {
        const moduleKey = toModuleKey(row.module || row.key);
        if (!moduleKey || isNonModulePermission(moduleKey)) return;
        next[moduleKey] = {
          view: Boolean(row.view),
          create: Boolean(row.create),
          update: Boolean(row.update),
          delete: getModuleCrudActions(moduleKey).includes("delete")
            ? Boolean(row.delete)
            : false,
        };
      });
    }
    (permissions || []).forEach((key) => {
      if (isNonModulePermission(key)) return;
      const moduleKey = toModuleKey(key);
      if (moduleKey && !next[moduleKey]) next[moduleKey] = defaultCrud();
    });
    setModuleCrud(next);
  };

  const fetchRoleAccess = async (roleName) => {
    try {
      const res = await axios.post(API_URL, {
        action: "get-role-access-by-role",
        role: roleName,
      });
      const data = parseApiResponse(res.data);
      if (!data?.success) return { id: null, access: [] };

      const payload = data.data || data.roleAccess || data;
      const access =
        payload.access ||
        payload.moduleAccess ||
        (Array.isArray(payload) ? payload : []);
      const accessId =
        payload._id || payload.id || data.id || data._id || null;

      return {
        id: accessId,
        access: Array.isArray(access) ? access : [],
      };
    } catch (err) {
      console.error("Fetch role access error:", err);
      return { id: null, access: [] };
    }
  };

  const fetchRole = useCallback(async () => {
    try {
      const res = await axios.post(API_URL, {
        action: "get-id-role",
        id,
      });
      const data = parseApiResponse(res.data);

      if (data.success && data.data) {
        const roleName = data.data.role || "";
        const permissions = (data.data.permissions || [])
          .map((key) => toCatalogPermissionKey(key))
          .filter((key) => key && !SCOPE_CHILD_KEYS.has(key));
        setRole(String(roleName).toUpperCase());

        const { id: accessId, access } = await fetchRoleAccess(
          String(roleName).toLowerCase(),
        );
        const fromAccess = (access || [])
          .map((row) => toCatalogPermissionKey(row.module || row.key))
          .filter(
            (key) =>
              key &&
              !SCOPE_CHILD_KEYS.has(key) &&
              !FLAG_PERMISSION_KEYS.has(key),
          );
        const mergedPermissions = Array.from(
          new Set([...permissions, ...fromAccess]),
        );
        setSelectedPermissions(mergedPermissions);
        setRoleAccessId(accessId);
        applyAccessRows(access, mergedPermissions);
      } else {
        toast.error(data.message || "Role not found");
        navigate("/roles");
      }
    } catch (err) {
      console.error("Fetch role error:", err);
      toast.error("Error loading role");
      navigate("/roles");
    } finally {
      setFetching(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const togglePermission = (key) => {
    setSelectedPermissions((prev) => {
      const moduleKey = toModuleKey(key);
      const exists =
        isKeySelected(prev, key) || Boolean(moduleCrud[moduleKey]);
      const next = exists ? prev.filter((p) => toModuleKey(p) !== moduleKey && p !== key) : [...prev, key];

      if (isNonModulePermission(key)) return next;

      if (!exists) {
        setModuleCrud((m) => ({ ...m, [moduleKey]: m[moduleKey] || defaultCrud() }));
      } else {
        const stillHas = next.some((p) => toModuleKey(p) === moduleKey);
        if (!stillHas) {
          setModuleCrud((m) => {
            const copy = { ...m };
            delete copy[moduleKey];
            return copy;
          });
        }
      }
      return next;
    });
  };

  const toggleGroup = (groupKeys) => {
    setSelectedPermissions((prev) => {
      const allSelected = groupKeys.every(
        (key) =>
          isKeySelected(prev, key) || Boolean(moduleCrud[toModuleKey(key)]),
      );
      if (allSelected) {
        groupKeys.forEach((key) => {
          if (isNonModulePermission(key)) return;
          const moduleKey = toModuleKey(key);
          setModuleCrud((m) => {
            const copy = { ...m };
            delete copy[moduleKey];
            return copy;
          });
        });
        return prev.filter(
          (key) => !groupKeys.some((groupKey) => isKeySelected([key], groupKey)),
        );
      }
      const next = [...prev];
      groupKeys.forEach((key) => {
        if (!isKeySelected(next, key)) next.push(key);
        if (isNonModulePermission(key)) return;
        const moduleKey = toModuleKey(key);
        setModuleCrud((m) => ({
          ...m,
          [moduleKey]: m[moduleKey] || defaultCrud(),
        }));
      });
      return next;
    });
  };

  const buildModuleAccess = () =>
    Object.entries(moduleCrud)
      .filter(([module]) => !isNonModulePermission(module))
      .map(([module, crud]) => {
        const allowed = new Set(getModuleCrudActions(module));
        return {
          module,
          view: allowed.has("view") ? Boolean(crud.view) : false,
          create: allowed.has("create") ? Boolean(crud.create) : false,
          update: allowed.has("update") ? Boolean(crud.update) : false,
          delete: allowed.has("delete") ? Boolean(crud.delete) : false,
        };
      });

  const permissionsForSave = () => {
    const keys = new Set();
    selectedPermissions.forEach((key) => {
      const catalogKey = toCatalogPermissionKey(key);
      if (catalogKey && !SCOPE_CHILD_KEYS.has(catalogKey)) {
        keys.add(catalogKey);
      }
    });
    Object.keys(moduleCrud).forEach((mod) => {
      if (isNonModulePermission(mod)) return;
      const catalogKey = toCatalogPermissionKey(mod);
      if (catalogKey) keys.add(catalogKey);
    });
    return Array.from(keys);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!role.trim()) {
      toast.error("Role is required");
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("Session expired. Please login again");
      navigate("/");
      return;
    }

    try {
      setLoading(true);
      const roleName = role.trim().toLowerCase();

      const res = await axios.post(API_URL, {
        action: "update-role",
        id,
        role: roleName,
        permissions: permissionsForSave(),
        updatedBy: currentUserId,
        currentUserId,
      });
      const data = parseApiResponse(res.data);

      if (!data.success) {
        toast.error(data.message || "Update failed");
        return;
      }

      const access = buildModuleAccess();
      if (access.length > 0) {
        const accessPayload = roleAccessId
          ? {
              action: "update-role-access",
              id: roleAccessId,
              role: roleName,
              access,
              updatedBy: currentUserId,
              currentUserId,
            }
          : {
              action: "create-role-access",
              role: roleName,
              access,
              createdBy: currentUserId,
              currentUserId,
            };

        const accessRes = await axios.post(API_URL, accessPayload);
        const accessData = parseApiResponse(accessRes.data);

        if (accessData.success !== true) {
          toast.warning(
            accessData.message ||
              "Role updated, but module access save failed.",
            { autoClose: 3500 },
          );
        }
      }

      toast.success(
        "Role updated successfully. Users with this role will receive updated permissions automatically.",
        { autoClose: 3000 },
      );
      navigate("/roles");
    } catch (err) {
      console.error("Update role error:", err);
      toast.error(err.response?.data?.message || "Error updating role");
    } finally {
      setLoading(false);
    }
  };

  const toggleCollapse = (groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const renderPermissionGroups = () =>
    PERMISSION_GROUPS.map((group) => {
      const groupKeys = group.permissions.map((perm) => perm.key);
      const selectedCount = groupKeys.filter(
        (key) =>
          isKeySelected(selectedPermissions, key) ||
          Boolean(moduleCrud[toModuleKey(key)]),
      ).length;
      const allSelected =
        groupKeys.length > 0 && selectedCount === groupKeys.length;
      const partiallySelected = selectedCount > 0 && !allSelected;
      const isCollapsed = Boolean(collapsedGroups[group.id]);

      return (
        <div
          key={group.id}
          className="mb-3"
          style={{
            border: "1px solid #e3e8ee",
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-between flex-wrap"
            style={{
              backgroundColor: "#1199B2",
              color: "#fff",
              padding: "12px 16px",
              cursor: "pointer",
            }}
            onClick={() => toggleCollapse(group.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleCollapse(group.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="d-flex align-items-center">
              <i
                className={`fa-solid fa-chevron-${isCollapsed ? "right" : "down"} mr-2`}
                style={{ fontSize: "12px", opacity: 0.85 }}
              />
              <i className={`${group.icon} mr-2`} />
              <strong style={{ fontSize: "14px" }}>{group.label}</strong>
              <span
                className="ml-2"
                style={{
                  fontSize: "11px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "10px",
                  padding: "2px 8px",
                }}
              >
                {selectedCount}/{groupKeys.length}
              </span>
            </div>
            <div
              className="form-check mb-0 mt-1 mt-sm-0"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                className="form-check-input"
                type="checkbox"
                id={`edit-group-${group.id}`}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = partiallySelected;
                }}
                onChange={() => toggleGroup(groupKeys)}
                disabled={loading}
              />
              <label
                className="form-check-label"
                htmlFor={`edit-group-${group.id}`}
                style={{ color: "#fff", fontSize: "12px", fontWeight: 600 }}
              >
                Select all
              </label>
            </div>
          </div>

          {!isCollapsed && (
            <div style={{ padding: "10px 14px 12px" }}>
              {group.permissions.map((perm) => {
                const moduleKey = toModuleKey(perm.key);
                const isSelected =
                  isKeySelected(selectedPermissions, perm.key) ||
                  Boolean(moduleCrud[moduleKey]);
                const showCrud =
                  isSelected && !isNonModulePermission(perm.key);

                return (
                  <div
                    key={perm.key}
                    className="mb-2"
                    style={{
                      border: isSelected
                        ? "1px solid #1199B2"
                        : "1px solid #e8edf1",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      backgroundColor: isSelected ? "#f7fcfd" : "#fff",
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <div className="form-check mb-0 mr-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`edit-${perm.key}`}
                          checked={isSelected}
                          onChange={() => togglePermission(perm.key)}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`edit-${perm.key}`}
                          style={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "#212529",
                          }}
                        >
                          {perm.label}
                        </label>
                      </div>
                      {showCrud ? (
                        <div className="mt-1 mt-md-0">
                          <CrudToggleRow
                            moduleKey={moduleKey}
                            crud={moduleCrud[moduleKey] || defaultCrud()}
                            disabled={loading}
                            onChange={setCrudForModule}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });

  const pageHeader = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">Edit Role</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/roles">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (fetching) {
    return (
      <div>
        {pageHeader}
        <div className="content">
          <div className="container-fluid">
            <div className="card card-primary card-outline">
              <div className="card-body text-center py-5">
                <PulseLoader color="#1199B2" size={10} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {pageHeader}

      <div className="content">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-lg-8">
                <div
                  className="card card-outline mb-3"
                  style={{ borderTop: "3px solid #1199B2" }}
                >
                  <div className="card-body">
                    <label
                      className="mb-1"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      Role Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. procurement team"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      required
                      disabled={loading}
                      style={{ maxWidth: "420px" }}
                    />
                    <small className="text-muted">
                      Saved in lowercase. Used for both menu permissions and
                      module access.
                    </small>
                  </div>
                </div>

                <div
                  className="card card-outline"
                  style={{ borderTop: "3px solid #003366" }}
                >
                  <div
                    className="card-header d-flex align-items-center justify-content-between"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <h3
                      className="card-title mb-0"
                      style={{ fontSize: "16px", fontWeight: 700 }}
                    >
                      <i className="fa-solid fa-shield-halved mr-2 text-primary" />
                      Module Permissions
                    </h3>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#1199B2",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {selectedModuleCount} selected
                    </span>
                  </div>
                  <div className="card-body">{renderPermissionGroups()}</div>
                </div>
              </div>

              <div className="col-lg-4">
                <div
                  className="card card-outline sticky-top"
                  style={{
                    borderTop: "3px solid #1199B2",
                    top: "70px",
                    zIndex: 10,
                  }}
                >
                  <div className="card-header" style={{ background: "#fff" }}>
                    <h3
                      className="card-title mb-0"
                      style={{ fontSize: "15px", fontWeight: 700 }}
                    >
                      Summary
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <div
                        className="text-muted"
                        style={{ fontSize: "12px", fontWeight: 600 }}
                      >
                        ROLE
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: 600 }}>
                        {role.trim() ? role.trim().toLowerCase() : "—"}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div
                        className="text-muted"
                        style={{ fontSize: "12px", fontWeight: 600 }}
                      >
                        MENU MODULES
                      </div>
                      <div style={{ fontSize: "20px", fontWeight: 700 }}>
                        {selectedModuleCount}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div
                        className="text-muted mb-1"
                        style={{ fontSize: "12px", fontWeight: 600 }}
                      >
                        ACTION ACCESS
                      </div>
                      {Object.keys(moduleCrud).length === 0 ? (
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                          Select modules to configure actions
                        </div>
                      ) : (
                        <div
                          style={{
                            maxHeight: "280px",
                            overflowY: "auto",
                            fontSize: "12px",
                          }}
                        >
                          {Object.entries(moduleCrud)
                            .sort(([a], [b]) =>
                              moduleLabel(a).localeCompare(
                                moduleLabel(b),
                                undefined,
                                { sensitivity: "base" },
                              ),
                            )
                            .map(([mod, crud]) => (
                            <div
                              key={mod}
                              className="d-flex justify-content-between align-items-center py-1"
                              style={{ borderBottom: "1px solid #f0f2f4" }}
                            >
                              <span style={{ fontWeight: 600 }}>
                                {moduleLabel(mod)}
                              </span>
                              <span className="text-muted">
                                {getModuleCrudActions(mod)
                                  .filter((k) => crud[k])
                                  .map((k) => k[0].toUpperCase())
                                  .join("") || "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="d-flex flex-column" style={{ gap: "8px" }}>
                      <button
                        type="submit"
                        className="btn btn-blue btn-block"
                        disabled={loading}
                      >
                        {loading ? (
                          <PulseLoader color="#ffffff" size={8} />
                        ) : (
                          <>
                            <i className="fa-solid fa-check mr-1" />
                            Update Role
                          </>
                        )}
                      </button>
                      <Link
                        to="/roles"
                        className="btn btn-outline-secondary btn-block"
                      >
                        Cancel
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRole;
